import type { BlockColor } from '@rock/types';

type ColorClasses = {
    text: string;
    bg?: string;
    border?: string;
    emphasisText?: string;
};

const tone = {
    gray: {
        text: 'text-zinc-700',
        bg: 'bg-zinc-100',
        border: 'border-zinc-200',
        emphasis: 'text-zinc-900',
    },
    brown: {
        text: 'text-amber-900',
        bg: 'bg-amber-50',
        border: 'border-amber-200/80',
        emphasis: 'text-amber-950',
    },
    orange: {
        text: 'text-orange-800',
        bg: 'bg-orange-50',
        border: 'border-orange-200/80',
        emphasis: 'text-orange-900',
    },
    yellow: {
        text: 'text-yellow-800',
        bg: 'bg-yellow-50',
        border: 'border-yellow-200/80',
        emphasis: 'text-yellow-900',
    },
    green: {
        text: 'text-green-800',
        bg: 'bg-green-50',
        border: 'border-green-200/80',
        emphasis: 'text-green-900',
    },
    blue: {
        text: 'text-blue-800',
        bg: 'bg-blue-50',
        border: 'border-blue-200/80',
        emphasis: 'text-blue-900',
    },
    purple: {
        text: 'text-violet-800',
        bg: 'bg-violet-50',
        border: 'border-violet-200/80',
        emphasis: 'text-violet-900',
    },
    pink: {
        text: 'text-pink-800',
        bg: 'bg-pink-50',
        border: 'border-pink-200/80',
        emphasis: 'text-pink-900',
    },
    red: {
        text: 'text-red-800',
        bg: 'bg-red-50',
        border: 'border-red-200/80',
        emphasis: 'text-red-900',
    },
    default: {
        text: 'text-zinc-900',
        bg: 'bg-transparent',
        border: 'border-zinc-200',
        emphasis: 'text-zinc-900',
    },
} as const;

export function getBlockColors(color: BlockColor): ColorClasses {
    if (color.endsWith('_background')) {
        const key = color.replace('_background', '') as keyof typeof tone;
        const toneValue = tone[key] ?? tone.default;
        return {
            text: toneValue.text,
            bg: toneValue.bg,
            border: toneValue.border,
            emphasisText: toneValue.emphasis,
        };
    }

    const toneValue = tone[color as keyof typeof tone] ?? tone.default;
    return {
        text: toneValue.text,
        border: toneValue.border,
        emphasisText: toneValue.emphasis,
    };
}

/** The background class for a `*_background` block or span, or undefined when it has no tint. */
export function getBackgroundClass(color: BlockColor): string | undefined {
    if (!color.endsWith('_background') || color === 'default_background') return undefined;
    return getBlockColors(color).bg;
}
